import { useRouter } from "expo-router";
import { useState } from "react";
import { StyleSheet, View } from "react-native";
import {
  Button,
  Card,
  Screen,
  Text,
  useTheme,
  useThemedStyles,
} from "~/components/ui";
import { useAuth } from "~/lib/auth";
import { registerDevicePushToken } from "~/features/notifications/push-registration";
import { spacing, type ThemeColors } from "~/styles/tokens";
import { notify } from "~/utils/notify";

export default function NotificationPermissionScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles();
  const { user, hasPro } = useAuth();
  const [loading, setLoading] = useState(false);
  const [granted, setGranted] = useState(false);

  const handleAllow = async () => {
    setLoading(true);
    try {
      const token = user?.id
        ? await registerDevicePushToken({
            userId: user.id,
            requestPermission: true,
          })
        : null;
      if (token) {
        setGranted(true);
      } else {
        notify(
          "Notifications disabled",
          "You can enable notifications later in your device settings to receive starter and recipe timer reminders.",
        );
        router.back();
      }
    } catch (error: unknown) {
      notify(
        "Error",
        error instanceof Error
          ? error.message
          : "Could not request permissions",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!hasPro) {
    return (
      <Screen width="narrow">
        <View style={styles.center}>
          <Text style={styles.bigEmoji}>🔔</Text>
          <Text variant="heading" align="center">
            Notifications are a Pro feature
          </Text>
          <Text variant="body" align="center" color={colors.muted}>
            DoughBro Pro syncs your starter and recipe timers, which is what
            lets us remind you when a wait step or a feeding is due — even with
            the app closed.
          </Text>
          <Button
            label="See DoughBro Pro"
            onPress={() => router.push("/subscription" as never)}
            variant="primary"
            fullWidth
            style={{ marginTop: spacing.lg, marginBottom: spacing.sm }}
          />
          <Button
            label="Not now"
            onPress={() => router.back()}
            variant="ghost"
            fullWidth
          />
        </View>
      </Screen>
    );
  }

  if (granted) {
    return (
      <Screen width="narrow">
        <View style={styles.center}>
          <Text style={styles.bigEmoji}>🔔</Text>
          <Text variant="heading" align="center">
            You&apos;re all set!
          </Text>
          <Text variant="body" align="center" color={colors.muted}>
            We&apos;ll remind you when a cook timer finishes. No spam, we
            promise!
          </Text>
          <Button
            label="Back to starter"
            onPress={() => router.back()}
            variant="primary"
            style={{ marginTop: spacing.lg }}
          />
        </View>
      </Screen>
    );
  }

  return (
    <Screen width="narrow">
      <View style={styles.center}>
        <Text style={styles.bigEmoji}>🔔</Text>
        <Text variant="heading" align="center">
          Feeding reminders
        </Text>
        <Card elevation="raised" style={styles.card}>
          <Text variant="label" style={{ marginBottom: spacing.md }}>
            Why we need notifications
          </Text>
          <View style={styles.reasonRow}>
            <Text>⏰</Text>
            <Text variant="body" style={styles.reasonText}>
              Alert you when recipe wait steps are done
            </Text>
          </View>
          <View style={styles.reasonRow}>
            <Text>😴</Text>
            <Text variant="body" style={styles.reasonText}>
              Remind you even when the app is closed
            </Text>
          </View>
          <View style={styles.reasonRow}>
            <Text>🚫</Text>
            <Text variant="body" style={styles.reasonText}>
              We&apos;ll never send marketing or irrelevant notifications
            </Text>
          </View>
        </Card>
        <Text
          variant="caption"
          align="center"
          color={colors.muted}
          style={styles.disclaimer}
        >
          Notifications are optional. You can turn them off at any time in your
          device&apos;s Settings.
        </Text>
        <Button
          label="Allow notifications"
          onPress={() => void handleAllow()}
          loading={loading}
          variant="primary"
          fullWidth
          style={{ marginBottom: spacing.sm }}
        />
        <Button
          label="Not now"
          onPress={() => router.back()}
          variant="ghost"
          fullWidth
        />
      </View>
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.lg,
      gap: spacing.md,
    },
    bigEmoji: {
      fontSize: 48,
      lineHeight: 56,
      textAlign: "center",
    },
    card: {
      width: "100%",
    },
    reasonRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: spacing.sm,
      marginBottom: spacing.sm,
    },
    reasonText: {
      flex: 1,
      color: c.heading,
    },
    disclaimer: {
      marginTop: -spacing.xs,
    },
  });
